import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@mdi/react'
import {
  mdiCamera,
  mdiClipboardOutline,
  mdiDownload,
  mdiShareVariant,
  mdiClose,
  mdiCheckCircleOutline,
  mdiAlertOutline,
} from '@mdi/js'
import { t } from '../i18n'
import '../skins/capture.css'

/**
 * Chooser sutil: portapapeles vs archivo (estilo classic / edge / studio).
 */
export function CaptureChooser({
  open,
  skin = 'classic',
  onClose,
  onClipboard,
  onFile,
  onShare,
}) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const node = (
    <div
      className={`udv-capture-root udv-capture--${skin}`}
      role="presentation"
      onClick={onClose}
      onContextMenu={(e) => {
        e.preventDefault()
        onClose && onClose()
      }}
    >
      <div
        className="udv-capture-card"
        role="dialog"
        aria-label={t('dialogs.captureTitle')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="udv-capture-head">
          <span className="udv-capture-title">
            <Icon
              path={mdiCamera}
              size={0.55}
              color="currentColor"
              style={{ marginRight: 6, verticalAlign: '-2px' }}
            />
            {t('dialogs.captureTitle')}
          </span>
          <button
            type="button"
            className="udv-capture-x"
            onClick={onClose}
            aria-label={t('common.close')}
            title={t('common.close')}
          >
            <Icon path={mdiClose} size={0.7} color="currentColor" />
          </button>
        </div>
        <div
          className={`udv-capture-actions${onShare ? ' is-three' : ''}`}
        >
          <button
            type="button"
            className="udv-capture-btn"
            onClick={() => onClipboard && onClipboard()}
          >
            <Icon path={mdiClipboardOutline} size={0.95} color="currentColor" />
            <span className="udv-capture-btn-label">{t('dialogs.captureClipboard')}</span>
          </button>
          <button
            type="button"
            className="udv-capture-btn"
            onClick={() => onFile && onFile()}
          >
            <Icon path={mdiDownload} size={0.95} color="currentColor" />
            <span className="udv-capture-btn-label">{t('dialogs.captureFile')}</span>
          </button>
          {onShare ? (
            <button
              type="button"
              className="udv-capture-btn"
              onClick={() => onShare()}
            >
              <Icon path={mdiShareVariant} size={0.95} color="currentColor" />
              <span className="udv-capture-btn-label">{t('share.short')}</span>
            </button>
          ) : null}
        </div>
        <div className="udv-capture-hint">{t('dialogs.captureHint')}</div>
      </div>
    </div>
  )

  return createPortal(node, document.body)
}

/**
 * Toast breve de resultado (mismo lenguaje visual por shell).
 * @param {{
 *   open: boolean,
 *   skin?: string,
 *   ok?: boolean,
 *   message?: string,
 *   onClose?: () => void,
 *   placement?: 'bottom'|'top',
 *   subtle?: boolean,
 *   durationMs?: number,
 * }} props
 */
export function CaptureToast({
  open,
  skin = 'classic',
  ok = true,
  message,
  onClose,
  placement = 'bottom',
  subtle = false,
  durationMs,
}) {
  const hideMs =
    durationMs != null
      ? durationMs
      : subtle
        ? ok
          ? 1600
          : 2800
        : ok
          ? 2200
          : 3800

  useEffect(() => {
    if (!open) return undefined
    const id = window.setTimeout(() => {
      if (onClose) onClose()
    }, hideMs)
    return () => window.clearTimeout(id)
  }, [open, ok, message, onClose, hideMs])

  if (!open || !message) return null

  const node = (
    <div
      className={`udv-capture-toast-rail udv-capture--${skin}${
        open ? ' is-visible' : ''
      }${placement === 'top' ? ' is-top' : ''}${subtle ? ' is-subtle' : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className={`udv-capture-toast${ok ? ' is-ok' : ' is-err'}`}>
        <span className="udv-capture-toast-icon" aria-hidden>
          <Icon
            path={ok ? mdiCheckCircleOutline : mdiAlertOutline}
            size={subtle ? 0.68 : 0.78}
            color="currentColor"
          />
        </span>
        <span className="udv-capture-toast-msg">{message}</span>
      </div>
    </div>
  )

  return createPortal(node, document.body)
}
